import React, { useState } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Sparkles, GraduationCap, ArrowRight } from "lucide-react";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import Skeleton from "../../../chat-companion/Skeleton/Skeleton";
import {
  colors,
  semanticColors,
  spacing,
  radii,
  typographyScale,
  shadowStyles,
  webTransitions,
} from "../../../../styles/design-system";

export interface LearningActivityCardProps {
  topic: string;
  description?: string;
  goals: string[];
  onPress: () => void;
  isGeneratingActivity: boolean;
}

export const LearningActivityCard = ({
  topic,
  description,
  goals,
  onPress,
  isGeneratingActivity,
}: LearningActivityCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  if (isGeneratingActivity) {
    return (
      <LinearGradient
        colors={[colors.purple[50], colors.purple[25]]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[styles.container, styles.generatingContainer]}>
        <View style={styles.header}>
          <Skeleton width={40} height={40} borderRadius={radii.lg} />
          <View style={styles.headerText}>
            <Skeleton width={120} height={12} borderRadius={radii.full} style={styles.skeletonLine} />
            <Skeleton width="85%" height={18} borderRadius={radii.sm} style={styles.skeletonLine} />
          </View>
        </View>
        <Skeleton width="100%" height={12} borderRadius={radii.full} style={styles.skeletonLine} />
        <Skeleton width="90%" height={12} borderRadius={radii.full} style={styles.skeletonLine} />
        <Skeleton width="95%" height={12} borderRadius={radii.full} style={styles.skeletonLine} />
        <View style={styles.goalsSection}>
          <Skeleton width={140} height={11} borderRadius={radii.full} style={styles.skeletonLine} />
          {[0, 1, 2].map((index) => (
            <View key={index} style={styles.goalItem}>
              <Skeleton width={20} height={20} borderRadius={10} />
              <Skeleton width="88%" height={12} borderRadius={radii.full} style={styles.skeletonGoalLine} />
            </View>
          ))}
        </View>
        <Skeleton width="100%" height={40} borderRadius={radii.sm} style={styles.skeletonButton} />
      </LinearGradient>
    );
  }

  return (
    <LinearGradient
      colors={[colors.purple[50], colors.purple[25]]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.container}>
      <View style={styles.header}>
        <View style={styles.iconBox}>
          <GraduationCap size={20} color={colors.purple[600]} />
        </View>

        <View style={styles.headerText}>
          <View style={styles.badgeContainer}>
            <Sparkles size={14} color={colors.purple[500]} />
            <CustomText text={"LEARNING ACTIVITY"} style={styles.badgeText} />
          </View>
          <CustomText style={styles.topicText} text={topic} useTranslationText={false} />
        </View>
      </View>

      {description && (
        <CustomText
          style={styles.descriptionText}
          text={description}
          useTranslationText={false}
          numberOfLines={4}
          ellipsizeMode="tail"
        />
      )}

      <View style={styles.goalsSection}>
        <CustomText style={styles.goalsHeader} text={"WHAT YOU'LL LEARN"} />
        {goals.slice(0, 5).map((goal, index) => (
          <View key={index} style={styles.goalItem}>
            <View style={styles.numberCircle}>
              <CustomText style={styles.numberText} text={index + 1} useTranslationText={false} />
            </View>
            <CustomText style={styles.goalText} text={goal} useTranslationText={false} />
          </View>
        ))}
      </View>

      <CustomButton
        style={[styles.button, isHovered && styles.buttonHovered]}
        styleType={"dark"}
        onPress={onPress}
        onHover={(hovered: boolean) => setIsHovered(hovered)}
        disabled={isGeneratingActivity}
        text={isGeneratingActivity ? "Generating Learning Activity" : "Start Learning Activity"}
        {...(isGeneratingActivity
          ? ({
              leftIcon: (
                <ActivityIndicator
                  color={colors.white}
                  style={{ marginRight: spacing.xs }}
                  accessibilityLabel="Generating learning activity"
                  accessibilityRole="progressbar"
                />
              ),
            } as any)
          : ({ rightIcon: <ArrowRight size={18} color={colors.white} style={{ marginLeft: spacing.xs }} /> } as any))}
        useTranslationText={false}
        accessibilityLabel={isGeneratingActivity ? "Generating Learning Activity" : "Start Learning Activity"}
        accessibilityRole="button"
        accessibilityHint="Starts the learning activity"
      />
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: radii.xl,
    borderWidth: 2,
    borderColor: colors.purple[300],
    padding: spacing.md,
    ...shadowStyles.md,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: spacing.xs,
  },
  iconBox: {
    width: 40,
    height: 40,
    backgroundColor: colors.purple[100],
    borderRadius: radii.lg,
    justifyContent: "center",
    alignItems: "center",
    marginRight: spacing.sm,
  },
  headerText: {
    flex: 1,
  },
  badgeContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: "700",
    color: colors.purple[600],
    letterSpacing: 0.5,
  },
  topicText: {
    fontSize: typographyScale.textBase.fontSize,
    fontWeight: "600",
    color: semanticColors.typography.displayPrimary,
  },
  descriptionText: {
    fontSize: typographyScale.textSm.fontSize,
    color: semanticColors.typography.textSecondary,
    lineHeight: typographyScale.textSm.lineHeight,
    marginBottom: spacing.sm,
  },
  goalsSection: {
    marginBottom: spacing.sm,
  },
  goalsHeader: {
    fontSize: 10,
    fontWeight: "700",
    color: semanticColors.typography.textSecondary,
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  goalItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: spacing.xs,
  },
  numberCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.purple[100],
    justifyContent: "center",
    alignItems: "center",
    marginRight: spacing.xs,
    marginTop: 2,
  },
  numberText: {
    fontSize: 10,
    fontWeight: "700",
    color: colors.purple[600],
  },
  goalText: {
    flex: 1,
    fontSize: typographyScale.textSm.fontSize,
    color: semanticColors.typography.textPrimary,
    lineHeight: typographyScale.textSm.lineHeight,
    marginTop: 2,
  },
  button: {
    backgroundColor: colors.purple[600],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: spacing.xs + spacing.xxs,
    borderRadius: radii.sm,
    ...webTransitions.fast,
  },
  buttonHovered: {
    backgroundColor: colors.accent.purple700,
    transform: [{ scale: 1.02 }],
  },
  generatingContainer: {
    opacity: 0.95,
    width: "100%",
    minWidth: "100%",
    alignSelf: "stretch",
  },
  skeletonLine: {
    marginBottom: spacing.xs,
  },
  skeletonGoalLine: {
    marginLeft: spacing.xs,
    marginTop: 4,
  },
  skeletonButton: {
    marginTop: spacing.xs,
  },
});
